/**
 * CreateTicketDialog — the entire "New Ticket" modal extracted from TicketsInboxPage.
 * Self-contained: handles file uploads & paste-image flow internally.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import RichTextEditor from '@/components/RichTextEditor';
import {
  Ticket,
  Plus,
  Paperclip,
  X,
  RefreshCw,
  ChevronRight,
} from 'lucide-react';
import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const PRIORITY_PILLS = [
  { k: 'low', label: 'Low', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', active: 'bg-emerald-600 text-white border-emerald-600' },
  { k: 'medium', label: 'Medium', cls: 'bg-amber-50 text-amber-700 border-amber-200', active: 'bg-amber-600 text-white border-amber-600' },
  { k: 'high', label: 'High', cls: 'bg-orange-50 text-orange-700 border-orange-200', active: 'bg-orange-600 text-white border-orange-600' },
  { k: 'critical', label: 'Critical', cls: 'bg-rose-50 text-rose-700 border-rose-200', active: 'bg-rose-600 text-white border-rose-600' },
];

export function CreateTicketDialog({
  open,
  onOpenChange,
  newTicket,
  setNewTicket,
  locations = [],
  availableModules = [],
  assignees = [],
  isStaff,
  isAgencyAdmin,
  isAgencyStaff,
  creating,
  onCreate,
  ticketTemplates = [],
}) {
  const staff = typeof isStaff === 'function' ? isStaff() : !!isStaff;
  // Agency users get access to the "submit on behalf of" + "internal only" fields.
  const agency = typeof isAgencyStaff === 'function' ? isAgencyStaff() : !!isAgencyStaff;

  // Bulk file upload from input change
  const handleFilesChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const f of files) {
      const mt = (f.type || '').toLowerCase();
      const max = mt.startsWith('image/') ? 5 * 1024 * 1024 : 10 * 1024 * 1024;
      if (f.size > max) {
        toast.error(`File too large: ${f.name}`);
        return;
      }
      if (!(mt.startsWith('image/') || mt.startsWith('video/') || mt === 'application/pdf')) {
        toast.error(`Unsupported file type: ${f.name}`);
        return;
      }
    }
    try {
      const uploaded = [];
      for (const f of files) {
        const init = await axios.post(`${API}/uploads/init`, { filename: f.name, mime_type: f.type, size: f.size });
        const uploadId = init.data.upload_id;
        const chunkSize = init.data.chunk_size;
        const totalChunks = Math.ceil(f.size / chunkSize);
        for (let i = 0; i < totalChunks; i++) {
          const chunk = f.slice(i * chunkSize, Math.min((i + 1) * chunkSize, f.size));
          const form = new FormData();
          form.append('file', chunk, f.name);
          await axios.post(`${API}/uploads/${uploadId}/chunk?chunk_index=${i}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        }
        const complete = await axios.post(`${API}/uploads/${uploadId}/complete?total_chunks=${totalChunks}`);
        uploaded.push(complete.data.attachment);
      }
      setNewTicket((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...uploaded] }));
      toast.success(`${uploaded.length} file(s) attached`);
      e.target.value = '';
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const handlePasteImage = async (file, insertAt) => {
    try {
      const mt = (file.type || '').toLowerCase();
      if (!mt.startsWith('image/')) return;
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Pasted image exceeds 5MB limit');
        return;
      }
      const init = await axios.post(`${API}/uploads/init`, {
        filename: file.name || 'pasted-image.png',
        mime_type: file.type,
        size: file.size,
      });
      const uploadId = init.data.upload_id;
      const chunkSize = init.data.chunk_size;
      const totalChunks = Math.ceil(file.size / chunkSize);
      for (let i = 0; i < totalChunks; i++) {
        const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
        const form = new FormData();
        form.append('file', chunk, file.name || 'pasted-image.png');
        await axios.post(`${API}/uploads/${uploadId}/chunk?chunk_index=${i}`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      const complete = await axios.post(`${API}/uploads/${uploadId}/complete?total_chunks=${totalChunks}`);
      const attachment = complete.data.attachment;
      // Inline-only — DON'T add to the ticket's attachments list. The image
      // renders in the body of the message and, on send, is CID-inlined by
      // the email service (which auto-discovers the uploaded file from disk
      // via the /api/uploads/{id}/download URL embedded in the HTML).
      if (typeof insertAt === 'function') {
        insertAt(attachment);
      } else {
        setNewTicket((prev) => ({
          ...prev,
          text: `${prev.text || ''}<p><img src="${attachment.url}" alt="${attachment.filename}" /></p>`,
        }));
      }
    } catch {
      toast.error('Failed to upload pasted image');
    }
  };

  const toggleFollower = (assignee) => {
    const list = newTicket.followers || [];
    const exists = list.find((f) => (f.user_id && f.user_id === assignee.id) || (f.email && f.email === assignee.email));
    const next = exists
      ? list.filter((f) => f !== exists)
      : [...list, { user_id: assignee.id || '', name: assignee.name || assignee.email || '', email: assignee.email || '' }];
    setNewTicket({ ...newTicket, followers: next });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 gap-0 sm:rounded-xl overflow-hidden h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col">
        {/* Gradient header */}
        <div className="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 text-white px-5 py-4 sm:px-6 sm:py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Ticket className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-white text-lg sm:text-xl font-semibold">
                Create a support ticket
              </DialogTitle>
              <p className="text-indigo-100 text-xs sm:text-sm mt-0.5">
                Tell us what&apos;s going on — we&apos;ll triage and assign an owner.
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-5 sm:px-6 space-y-5">
          {/* Section: Where */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px]">1</span>
              Where &amp; who
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ticket-location" className="text-xs text-slate-600">Location *</Label>
                <Select value={newTicket.location_id} onValueChange={(value) => setNewTicket({ ...newTicket, location_id: value })}>
                  <SelectTrigger id="ticket-location" data-testid="ticket-location" className="mt-1">
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="ticket-reported-person" className="text-xs text-slate-600">Reported by</Label>
                <Input
                  id="ticket-reported-person"
                  placeholder="e.g. John Smith"
                  value={newTicket.reported_person_name}
                  onChange={(e) => setNewTicket({ ...newTicket, reported_person_name: e.target.value })}
                  data-testid="new-ticket-reported-person"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Agency-only: submit on behalf of + internal ticket */}
            {agency && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-3" data-testid="agency-fields">
                <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold">Agency options</div>

                <div>
                  <Label className="text-xs text-slate-600">Submit on behalf of</Label>
                  <Select
                    value={newTicket.on_behalf_of_user_id || '__self__'}
                    onValueChange={(v) => setNewTicket({ ...newTicket, on_behalf_of_user_id: v === '__self__' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="new-ticket-on-behalf-of">
                      <SelectValue placeholder="Myself" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__self__">Myself (default)</SelectItem>
                      {assignees
                        .filter((a) => !newTicket.location_id || (a.location_ids || []).includes(newTicket.location_id))
                        .map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name || a.email}{a.email ? ` — ${a.email}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Ticket appears as though the selected user created it. The audit log records that you performed the action.
                  </p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={!!newTicket.internal_only}
                    onCheckedChange={(v) => setNewTicket({ ...newTicket, internal_only: !!v })}
                    data-testid="new-ticket-internal-only"
                  />
                  <div className="text-sm">
                    <div className="text-slate-800 font-medium">Internal ticket (agency-only)</div>
                    <div className="text-[11px] text-slate-500">Hidden from Account Admin / Account User at this location.</div>
                  </div>
                </label>
              </div>
            )}

            {/* Followers selector — CC users on every reply (all users can add) */}
            {assignees.length > 0 && (
              <div>
                <Label className="text-xs text-slate-600">Followers (CC on every reply)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="mt-1 w-full justify-between h-auto min-h-9 py-1.5 px-3 font-normal"
                      data-testid="new-ticket-followers"
                    >
                      <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-1rem)]">
                        {(newTicket.followers || []).length > 0 ? (
                          (newTicket.followers || []).map((f) => (
                            <Badge
                              key={(f.user_id || '') + (f.email || '')}
                              variant="outline"
                              className="bg-cyan-50 text-cyan-700 border-cyan-200 text-[11px]"
                            >
                              {f.name || f.email}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-slate-400 text-sm">Optional — add follower(s)</span>
                        )}
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 rotate-90 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="start">
                    <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                      Followers receive a copy of every email reply
                    </div>
                    <div className="max-h-60 overflow-auto p-1">
                      {assignees.map((a) => {
                        const list = newTicket.followers || [];
                        const checked = list.some((f) => (f.user_id && f.user_id === a.id) || (f.email && f.email === a.email));
                        return (
                          <label
                            key={a.id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleFollower(a)}
                              data-testid={`new-ticket-follower-opt-${a.id}`}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm text-slate-700 truncate">{a.name || a.email}</div>
                              {a.name && a.email && (
                                <div className="text-[11px] text-slate-400 truncate">{a.email}</div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </section>

          <div className="h-px bg-slate-100" />

          {/* Section: What */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px]">2</span>
              What&apos;s the issue
            </div>
            <div>
              <Label htmlFor="ticket-subject" className="text-xs text-slate-600">Subject</Label>
              <Input
                id="ticket-subject"
                placeholder="Short, descriptive summary"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="new-ticket-subject"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ticket-module" className="text-xs text-slate-600">Module *</Label>
                <Select value={newTicket.module} onValueChange={(value) => setNewTicket({ ...newTicket, module: value })}>
                  <SelectTrigger id="ticket-module" data-testid="ticket-module" className="mt-1">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModules.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Priority</Label>
                <div className="mt-1 flex flex-wrap gap-1.5" data-testid="priority-picker">
                  {PRIORITY_PILLS.map((p) => {
                    const selected = newTicket.priority === p.k;
                    return (
                      <button
                        key={p.k}
                        type="button"
                        onClick={() => setNewTicket({ ...newTicket, priority: p.k })}
                        className={`h-9 px-3 rounded-full border text-xs font-medium transition-colors ${
                          selected ? p.active : p.cls + ' hover:brightness-95'
                        }`}
                        data-testid={`priority-pill-${p.k}`}
                      >
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

          <div className="h-px bg-slate-100" />

          {/* Section: Description */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px]">3</span>
              Description
              {ticketTemplates.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-auto h-7 px-2.5 text-[11px] font-medium normal-case tracking-normal text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      data-testid="ticket-template-picker"
                    >
                      <Paperclip className="w-3 h-3 mr-1" />
                      Use template
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-0" align="end">
                    <div className="px-3 py-2 border-b border-slate-100 text-xs font-medium text-slate-600">
                      Pre-fill the description
                    </div>
                    <div className="max-h-72 overflow-auto p-1">
                      {ticketTemplates.map((t) => {
                        const moduleMatch = !t.module || t.module === newTicket.module;
                        return (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => {
                              setNewTicket({
                                ...newTicket,
                                text: t.body || '',
                                module: t.module || newTicket.module,
                              });
                            }}
                            className="w-full text-left px-2 py-1.5 rounded hover:bg-indigo-50 text-sm text-slate-700"
                            data-testid={`ticket-template-${t.id}`}
                            title={t.module ? `Scoped to module: ${t.module}` : 'Any module'}
                          >
                            <div className="font-medium truncate">{t.title}</div>
                            <div className="text-[11px] text-slate-400 truncate">
                              {t.module ? `· ${t.module}` : '· any module'} · {moduleMatch ? 'match' : 'different module'}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <Label htmlFor="ticket-text" className="sr-only">Description *</Label>
            <div className="rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/30">
              <RichTextEditor
                content={newTicket.text}
                onChange={(html) => setNewTicket({ ...newTicket, text: html })}
                placeholder="Describe the issue — include steps to reproduce, what you expected, and screenshots if possible. Tip: you can paste images directly."
                minHeight={180}
                onPasteImage={handlePasteImage}
              />
            </div>
          </section>

          <div className="h-px bg-slate-100" />

          {/* Section: Attachments */}
          <section className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              <span className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px]">4</span>
              Attachments
              <span className="ml-auto text-[11px] text-slate-400 normal-case tracking-normal">Images up to 5MB · video/PDF up to 10MB</span>
            </div>
            <label
              htmlFor="ticket-attachments"
              className="flex items-center justify-center gap-2 w-full rounded-lg border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30 text-slate-500 hover:text-indigo-600 transition-colors px-3 py-4 cursor-pointer text-sm"
              data-testid="new-ticket-attachments-dropzone"
            >
              <Paperclip className="w-4 h-4" />
              <span>Click to add files, or paste images directly into the description</span>
            </label>
            <input
              id="ticket-attachments"
              type="file"
              multiple
              accept="image/*,video/*,application/pdf"
              className="hidden"
              onChange={handleFilesChange}
              data-testid="new-ticket-attachments"
            />
            {newTicket.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="new-ticket-attachment-chips">
                {newTicket.attachments.map((a, idx) => (
                  <span
                    key={`${a.id || a.filename}-${idx}`}
                    className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1 py-1"
                  >
                    <Paperclip className="w-3 h-3" />
                    <span className="truncate max-w-[180px]">{a.filename}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setNewTicket((prev) => ({
                          ...prev,
                          attachments: prev.attachments.filter((_, i) => i !== idx),
                        }))
                      }
                      className="ml-0.5 rounded-full hover:bg-slate-200 p-0.5"
                      aria-label="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Sticky footer */}
        <div className="border-t border-slate-100 bg-white px-5 py-3 sm:px-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full sm:w-auto"
            data-testid="cancel-create-ticket"
          >
            Cancel
          </Button>
          <Button
            onClick={onCreate}
            disabled={creating}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg"
            data-testid="submit-ticket"
          >
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1.5" />
                Create ticket
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default CreateTicketDialog;
